import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Trash2, Edit3, Check, X, Settings, GripVertical, Lightbulb, CheckSquare, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import Sidebar from './Sidebar';
import Header from './Header';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import PageHeader from './ui/PageHeader';
import CustomDropdown from './ui/CustomDropdown';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const gerarIdUnico = () => {
  return `tarefa-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/* ── Color Maps ── */
const COLUMN_COLORS = {
  gray:   { hex: '#6B7280', bg: 'rgba(107,114,128,0.10)' },
  yellow: { hex: '#FBBF24', bg: 'rgba(251,191,36,0.10)' },
  blue:   { hex: '#0DAEFF', bg: 'rgba(13,174,255,0.10)' },
  purple: { hex: '#A78BFA', bg: 'rgba(167,139,250,0.10)' },
  green:  { hex: '#34D399', bg: 'rgba(52,211,153,0.10)' },
  red:    { hex: '#F87171', bg: 'rgba(248,113,113,0.10)' },
  orange: { hex: '#FB923C', bg: 'rgba(251,146,60,0.10)' },
  pink:   { hex: '#F472B6', bg: 'rgba(244,114,182,0.10)' },
};

const NOTE_COLORS = {
  amarelo: { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.25)', accent: '#FBBF24' },
  rosa:    { bg: 'rgba(244,114,182,0.08)', border: 'rgba(244,114,182,0.25)', accent: '#F472B6' },
  azul:    { bg: 'rgba(13,174,255,0.08)', border: 'rgba(13,174,255,0.25)', accent: '#0DAEFF' },
  verde:   { bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.25)', accent: '#34D399' },
  roxo:    { bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.25)', accent: '#A78BFA' },
  laranja: { bg: 'rgba(251,146,60,0.08)', border: 'rgba(251,146,60,0.25)', accent: '#FB923C' },
};

const PROGRESS_GRADIENTS = [
  'linear-gradient(90deg, #34D399, #22D3EE)',
  'linear-gradient(90deg, #FBBF24, #FB923C)',
  'linear-gradient(90deg, #0DAEFF, #A78BFA)',
];

/* ── Shared inline styles ── */
const S = {
  input: {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--dp-bg-input, #252525)',
    border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))',
    borderRadius: '8px',
    color: 'var(--dp-text-primary, #F5F5F5)',
    fontFamily: 'var(--dp-font, Outfit, sans-serif)',
    fontSize: '13px',
    transition: 'all 0.3s',
    outline: 'none',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--dp-text-secondary, #A0A0A0)',
    marginBottom: '6px',
    fontFamily: 'var(--dp-font)',
  },
  // Padrão premium .dp-btn-cta (ver DESIGN_SYSTEM.md §5) — versão objeto p/ spreads.
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '9px 18px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
    border: 'none',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.2px',
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    boxShadow: 'none',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  },
  btnSecondary: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.10))',
    background: 'transparent',
    color: 'var(--dp-text-secondary, #A0A0A0)',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    transition: 'all 0.25s',
  },
  btnCancel: {
    padding: '9px 20px',
    borderRadius: '8px',
    border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.10))',
    background: 'transparent',
    color: 'var(--dp-text-secondary, #A0A0A0)',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: 'var(--dp-font)',
    cursor: 'pointer',
    transition: 'all 0.25s',
  },
};

/* ───────────────────────── Component ───────────────────────── */
const QuadroIdeiasPageKanban = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle(t('ideasBoard.title'));
  const { sidebarWidth, isMobile } = useSidebar();

  /* ── State ── */
  const [notas, setNotas] = useState([]);
  const [colunas, setColunas] = useState([]);
  const [canais, setCanais] = useState([]);
  const [espacoSelecionado, setEspacoSelecionado] = useState('geral');
  const [editandoNotaId, setEditandoNotaId] = useState(null);
  const [editandoTitulo, setEditandoTitulo] = useState('');
  const [editandoConteudo, setEditandoConteudo] = useState('');
  const [editandoTarefaId, setEditandoTarefaId] = useState(null);
  const [editandoTarefaNotaId, setEditandoTarefaNotaId] = useState(null);
  const [editandoTarefaTexto, setEditandoTarefaTexto] = useState('');
  const [editandoConteudoNotaId, setEditandoConteudoNotaId] = useState(null);
  const [editandoConteudoTexto, setEditandoConteudoTexto] = useState('');
  const [showModalColuna, setShowModalColuna] = useState(false);
  const [colunaEditando, setColunaEditando] = useState(null);
  const [formColuna, setFormColuna] = useState({ nome: '', cor: 'blue' });
  const [showModalNota, setShowModalNota] = useState(false);
  const [colunaParaNovaNota, setColunaParaNovaNota] = useState(null);
  const [tipoNovaNota, setTipoNovaNota] = useState('nota_simples');
  const [formNota, setFormNota] = useState({ titulo: '', cor: 'amarelo' });
  const [cardOrder, setCardOrder] = useState({}); // { columnId: [cardId1, cardId2, ...] }

  /* ── Fetch canais ── */
  useEffect(() => {
    const fetchCanais = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/canais`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) setCanais(data.canais || []);
      } catch (error) {
        console.error('Erro ao buscar canais:', error);
      }
    };
    fetchCanais();
  }, []);

  const buildCanalParam = useCallback(() => {
    return espacoSelecionado === 'geral' ? 'canal_id=geral' : espacoSelecionado ? `canal_id=${espacoSelecionado}` : '';
  }, [espacoSelecionado]);

  const fetchColunas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/quadro-ideias/colunas?${buildCanalParam()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setColunas(data.colunas || []);
    } catch (error) {
      console.error('Erro ao buscar colunas:', error);
    }
  }, [buildCanalParam]);

  const fetchNotas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/quadro-ideias?${buildCanalParam()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setNotas(data.notas || []);
    } catch (error) {
      console.error('Erro ao buscar notas:', error);
    }
  }, [buildCanalParam]);

  /* ── Fetch colunas + notas ── */
  useEffect(() => {
    fetchColunas();
    fetchNotas();
  }, [espacoSelecionado, fetchColunas, fetchNotas]);

  /* ── Rebuild card order when columns or notes change ── */
  const notasTagKey = notas.map(n => `${n.id}:${(n.tags || [])[0] || ''}`).join('|');
  useEffect(() => {
    if (colunas.length === 0) return;
    setCardOrder(prev => {
      const orderMap = {};
      colunas.forEach(col => {
        const notasInCol = notas.filter(n => (n.tags || []).includes(col.id));
        if (prev[col.id]) {
          // Preserve existing order, remove stale, append new
          const valid = prev[col.id].filter(id => notasInCol.some(n => n.id === id));
          const newIds = notasInCol.filter(n => !valid.includes(n.id)).map(n => n.id);
          orderMap[col.id] = [...valid, ...newIds];
        } else {
          orderMap[col.id] = notasInCol.map(n => n.id);
        }
      });
      return orderMap;
    });
  }, [colunas.length, notasTagKey]);

  /* ── CRUD Colunas ── */
  const abrirModalNovaColuna = () => {
    setColunaEditando(null);
    setFormColuna({ nome: '', cor: 'blue' });
    setShowModalColuna(true);
  };

  const abrirModalEditarColuna = (coluna) => {
    setColunaEditando(coluna);
    setFormColuna({ nome: coluna.nome, cor: coluna.cor });
    setShowModalColuna(true);
  };

  const salvarColuna = async () => {
    if (!formColuna.nome.trim()) {
      toast.error(t('ideasBoard.column_name_required'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formColuna,
        canal_id: espacoSelecionado === 'geral' ? null : espacoSelecionado,
        ordem: colunaEditando ? colunaEditando.ordem : colunas.length
      };
      let response;
      if (colunaEditando) {
        response = await fetch(`${BACKEND_URL}/api/quadro-ideias/colunas/${colunaEditando.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(formColuna)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/quadro-ideias/colunas`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      const data = await response.json();
      if (data.success) {
        toast.success(colunaEditando ? t('ideasBoard.column_updated') : t('ideasBoard.column_created'));
        setShowModalColuna(false);
        fetchColunas();
      }
    } catch (error) {
      console.error('Erro ao salvar coluna:', error);
      toast.error(t('ideasBoard.error_save_column'));
    }
  };

  const deletarColuna = async (colunaId) => {
    if (!window.confirm(t('ideasBoard.delete_column_confirm'))) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/quadro-ideias/colunas/${colunaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(t('ideasBoard.column_deleted'));
        fetchColunas();
      }
    } catch (error) {
      console.error('Erro ao deletar coluna:', error);
      toast.error(t('ideasBoard.error_delete_column'));
    }
  };

  /* ── CRUD Notas ── */
  const abrirModalNovaNota = (colunaId, tipo) => {
    setColunaParaNovaNota(colunaId);
    setTipoNovaNota(tipo);
    setFormNota({ titulo: tipo === 'checklist' ? t('ideasBoard.new_checklist') : t('ideasBoard.new_note'), cor: 'amarelo' });
    setShowModalNota(true);
  };

  const criarNota = async () => {
    if (!formNota.titulo.trim()) {
      toast.error(t('ideasBoard.card_title_required'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        tipo: tipoNovaNota,
        titulo: formNota.titulo,
        conteudo: tipoNovaNota === 'nota_simples' ? t('ideasBoard.default_content') : '',
        cor: formNota.cor,
        canal_id: espacoSelecionado === 'geral' ? null : espacoSelecionado,
        tags: [colunaParaNovaNota],
        tarefas: tipoNovaNota === 'checklist' ? [
          { id: gerarIdUnico(), texto: t('ideasBoard.new_task'), concluida: false }
        ] : []
      };
      const response = await fetch(`${BACKEND_URL}/api/quadro-ideias`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(t('ideasBoard.note_created'));
        setShowModalNota(false);
        fetchNotas();
      }
    } catch (error) {
      console.error('Erro ao criar nota:', error);
      toast.error(t('ideasBoard.error_create_note'));
    }
  };

  const atualizarNota = async (notaId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/quadro-ideias/${notaId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        // Update local state instead of full refetch (prevents flicker)
        setNotas(prev => prev.map(n =>
          n.id === notaId ? { ...n, ...updates } : n
        ));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao atualizar nota:', error);
      toast.error(t('ideasBoard.error_update_note'));
      return false;
    }
  };

  const deletarNota = async (notaId) => {
    if (!window.confirm(t('ideasBoard.delete_note_confirm'))) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/quadro-ideias/${notaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(t('ideasBoard.note_deleted'));
        fetchNotas();
      }
    } catch (error) {
      console.error('Erro ao deletar nota:', error);
      toast.error(t('ideasBoard.error_delete_note'));
    }
  };

  const moverNota = async (nota, novaColunaId) => {
    // Preserve user tags (tags[1+]), only replace column tag (tags[0])
    const userTags = (nota.tags || []).slice(1);
    await atualizarNota(nota.id, { tags: [novaColunaId, ...userTags] });
  };

  /* ── Inline editing: título ── */
  const iniciarEdicao = (nota) => {
    setEditandoNotaId(nota.id);
    setEditandoTitulo(nota.titulo);
    setEditandoConteudo(nota.conteudo || '');
  };

  const salvarEdicao = async () => {
    if (!editandoNotaId) return;
    await atualizarNota(editandoNotaId, { titulo: editandoTitulo, conteudo: editandoConteudo });
    setEditandoNotaId(null);
  };

  const cancelarEdicao = () => {
    setEditandoNotaId(null);
    setEditandoTitulo('');
    setEditandoConteudo('');
  };

  /* ── Inline editing: conteúdo ── */
  const iniciarEdicaoConteudo = (nota) => {
    setEditandoConteudoNotaId(nota.id);
    setEditandoConteudoTexto(nota.conteudo || '');
  };

  const salvarEdicaoConteudo = async (notaId) => {
    await atualizarNota(notaId, { conteudo: editandoConteudoTexto });
    setEditandoConteudoNotaId(null);
    setEditandoConteudoTexto('');
  };

  const cancelarEdicaoConteudo = () => {
    setEditandoConteudoNotaId(null);
    setEditandoConteudoTexto('');
  };

  /* ── Checklist tasks ── */
  const toggleTarefa = async (nota, tarefaId) => {
    const novasTarefas = nota.tarefas.map(t2 => t2.id === tarefaId ? { ...t2, concluida: !t2.concluida } : t2);
    await atualizarNota(nota.id, { tarefas: novasTarefas });
  };

  const adicionarTarefa = async (nota) => {
    setEditandoTarefaNotaId(null);
    setEditandoTarefaId(null);
    setEditandoTarefaTexto('');
    const novoId = gerarIdUnico();
    const novasTarefas = [...(nota.tarefas || []), { id: novoId, texto: t('ideasBoard.new_task'), concluida: false }];
    const success = await atualizarNota(nota.id, { tarefas: novasTarefas });
    if (success) {
      setTimeout(() => {
        setEditandoTarefaNotaId(nota.id);
        setEditandoTarefaId(novoId);
        setEditandoTarefaTexto(t('ideasBoard.new_task'));
      }, 100);
    }
  };

  const iniciarEdicaoTarefa = (notaId, tarefaId, textoAtual) => {
    if (editandoTarefaNotaId !== null && editandoTarefaId !== null) return;
    setEditandoTarefaNotaId(notaId);
    setEditandoTarefaId(tarefaId);
    setEditandoTarefaTexto(textoAtual);
  };

  const salvarEdicaoTarefa = async (nota) => {
    if (editandoTarefaNotaId !== nota.id || !editandoTarefaId) return;
    const tarefaExiste = nota.tarefas?.some(t2 => t2.id === editandoTarefaId);
    if (!tarefaExiste) { cancelarEdicaoTarefa(); return; }
    const novasTarefas = nota.tarefas.map(t2 => t2.id === editandoTarefaId ? { ...t2, texto: editandoTarefaTexto } : t2);
    await atualizarNota(nota.id, { tarefas: novasTarefas });
    setEditandoTarefaNotaId(null);
    setEditandoTarefaId(null);
    setEditandoTarefaTexto('');
  };

  const cancelarEdicaoTarefa = () => {
    setEditandoTarefaNotaId(null);
    setEditandoTarefaId(null);
    setEditandoTarefaTexto('');
  };

  /* ── Helpers ── */
  const calcularProgresso = (tarefas) => {
    if (!tarefas || tarefas.length === 0) return 0;
    return Math.round((tarefas.filter(t2 => t2.concluida).length / tarefas.length) * 100);
  };

  const getNotasPorColuna = (colunaId) => {
    const order = cardOrder[colunaId] || [];
    const notasInCol = notas.filter(n => (n.tags || []).includes(colunaId));
    // Sort by cardOrder, append any unordered cards at end
    const ordered = order.map(id => notasInCol.find(n => n.id === id)).filter(Boolean);
    const unordered = notasInCol.filter(n => !order.includes(n.id));
    return [...ordered, ...unordered];
  };

  const getColColor = (cor) => COLUMN_COLORS[cor] || COLUMN_COLORS.gray;
  const getNoteColor = (cor) => NOTE_COLORS[cor] || NOTE_COLORS.amarelo;

  const getProgressGradient = (pct) => {
    if (pct >= 100) return PROGRESS_GRADIENTS[0];
    if (pct >= 50) return PROGRESS_GRADIENTS[1];
    return PROGRESS_GRADIENTS[2];
  };

  const getTypeConfig = (tipo) => {
    if (tipo === 'checklist') return { icon: <CheckSquare size={12} />, label: t('ideasBoard.checklist'), color: 'var(--dp-green-500, #34D399)' };
    if (tipo === 'nota_simples') return { icon: <StickyNote size={12} />, label: t('ideasBoard.note'), color: 'var(--dp-yellow-500, #FBBF24)' };
    return { icon: <Lightbulb size={12} />, label: t('ideasBoard.idea'), color: 'var(--dp-purple-500, #A78BFA)' };
  };

  /* ── Drag & Drop handlers ── */
  const onDragEnd = async (result) => {
    const { draggableId, source, destination } = result;

    // Cancelled drop
    if (!destination) return;

    // No change
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const nota = notas.find(n => n.id === draggableId);
    if (!nota) return;

    const sourceColumnId = source.droppableId;
    const destColumnId = destination.droppableId;
    const isSameColumn = sourceColumnId === destColumnId;

    // Snapshot for revert
    const prevOrder = { ...cardOrder };

    // Update local card order optimistically
    setCardOrder(prev => {
      const newOrder = { ...prev };
      const sourceOrder = [...(newOrder[sourceColumnId] || [])];
      sourceOrder.splice(source.index, 1);

      const destOrder = isSameColumn ? sourceOrder : [...(newOrder[destColumnId] || [])];
      destOrder.splice(destination.index, 0, draggableId);

      newOrder[sourceColumnId] = sourceOrder;
      if (!isSameColumn) newOrder[destColumnId] = destOrder;

      return newOrder;
    });

    // Cross-column move: update tags + persist to server
    if (!isSameColumn) {
      const userTags = (nota.tags || []).slice(1);
      setNotas(prev => prev.map(n =>
        n.id === draggableId ? { ...n, tags: [destColumnId, ...userTags] } : n
      ));

      try {
        await moverNota(nota, destColumnId);
        toast.success(t('ideasBoard.card_moved'));
      } catch (e) {
        // Revert order and tags on error
        setCardOrder(prevOrder);
        fetchNotas();
        toast.error(t('ideasBoard.error_move_card'));
      }
    }
  };

  /* ── Stagger animation style ── */
  const colAnimStyle = (i) => ({
    animation: `dpKanbanFadeIn 0.35s ease both`,
    animationDelay: `${i * 0.06}s`,
  });

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary, #141414)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        transition: 'margin-left 0.3s ease',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* ── Page Header ── */}
        <PageHeader
          icon={<Lightbulb size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title={t('ideasBoard.title')}
          description={t('ideasBoard.subtitle')}
        >
          <div style={{ minWidth: '200px' }}>
            <CustomDropdown
              value={espacoSelecionado}
              onChange={(val) => setEspacoSelecionado(val)}
              options={[
                { value: 'geral', label: `📋 ${t('ideasBoard.workspace_general')}` },
                ...canais.map(c => ({ value: c.id, label: `🎥 ${c.nome}` }))
              ]}
              placeholder={t('ideasBoard.workspace_select')}
            />
          </div>
          <button
            onClick={abrirModalNovaColuna}
            style={S.btnPrimary}
          >
            <Plus size={16} />
            {t('ideasBoard.new_column')}
          </button>
        </PageHeader>

        {/* ── Kanban Board with Drag & Drop ── */}
        <DragDropContext onDragEnd={onDragEnd}>
          <div style={{
            flex: 1,
            display: 'flex',
            gap: '14px',
            overflowX: 'auto',
            overflowY: 'visible',
            paddingBottom: '8px',
            minHeight: 0,
          }}>
            {colunas.map((coluna, colIdx) => {
              const notasDaColuna = getNotasPorColuna(coluna.id);
              const colColor = getColColor(coluna.cor);

              return (
                <Droppable droppableId={coluna.id} key={coluna.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        minWidth: '300px',
                        maxWidth: '320px',
                        flexShrink: 0,
                        background: snapshot.isDraggingOver
                          ? 'var(--dp-bg-card-hover, #262626)'
                          : 'var(--dp-bg-card, #1E1E1E)',
                        border: snapshot.isDraggingOver
                          ? `1px solid ${colColor.hex}40`
                          : '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))',
                        borderRadius: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: 'calc(100vh - 180px)',
                        transition: 'all 0.25s',
                        ...colAnimStyle(colIdx),
                      }}
                    >
                      {/* ── Column Header ── */}
                      <div style={{ padding: '14px 16px 10px', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '4px',
                              height: '20px',
                              borderRadius: '2px',
                              background: colColor.hex,
                            }} />
                            <span style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              letterSpacing: '0.3px',
                              color: 'var(--dp-text-primary, #F5F5F5)',
                              fontFamily: 'var(--dp-font)',
                              textTransform: 'uppercase',
                            }}>
                              {coluna.nome}
                            </span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              fontSize: '10px',
                              fontWeight: 600,
                              background: 'var(--dp-border-subtle)',
                              color: 'var(--dp-text-tertiary, #5A5A5A)',
                              fontFamily: 'var(--dp-font)',
                            }}>
                              {notasDaColuna.length}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              onClick={() => abrirModalEditarColuna(coluna)}
                              style={{
                                width: '26px', height: '26px', borderRadius: '6px',
                                border: '1px solid transparent', background: 'transparent',
                                color: 'var(--dp-text-tertiary)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
                            >
                              <Settings size={12} />
                            </button>
                            <button
                              onClick={() => deletarColuna(coluna.id)}
                              style={{
                                width: '26px', height: '26px', borderRadius: '6px',
                                border: '1px solid transparent', background: 'transparent',
                                color: 'var(--dp-text-tertiary)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(248,113,113,0.3)'; e.currentTarget.style.color = '#F87171'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* ── Column Body (cards) ── */}
                      <div className="kanban-col-body" style={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '4px 10px 10px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minHeight: '60px',
                      }}>
                        {notasDaColuna.length === 0 && !snapshot.isDraggingOver && (
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: '40px 20px', textAlign: 'center',
                          }}>
                            <div>
                              <div style={{ fontSize: '32px', opacity: 0.3, marginBottom: '8px' }}>📦</div>
                              <div style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                                {t('ideasBoard.empty_column')}
                              </div>
                            </div>
                          </div>
                        )}

                        {notasDaColuna.map((nota, noteIdx) => (
                          <Draggable draggableId={nota.id} index={noteIdx} key={nota.id}>
                            {(dragProvided, dragSnapshot) => {
                              const noteColor = getNoteColor(nota.cor);
                              const typeConfig = getTypeConfig(nota.tipo);
                              const isEditing = editandoNotaId === nota.id;

                              const child = (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  style={{
                                    background: dragSnapshot.isDragging
                                      ? 'var(--dp-bg-card-hover, #262626)'
                                      : 'var(--dp-bg-elevated, #2A2A2A)',
                                    border: dragSnapshot.isDragging
                                      ? '2px solid var(--dp-blue-500, #0DAEFF)'
                                      : `1px solid ${noteColor.border}`,
                                    borderRadius: '8px',
                                    padding: '12px 14px',
                                    cursor: dragSnapshot.isDragging ? 'grabbing' : 'grab',
                                    transition: dragSnapshot.isDragging ? 'none' : 'all 0.25s',
                                    userSelect: 'none',
                                    ...dragProvided.draggableProps.style,
                                    // AFTER spread — these MUST override library styles
                                    zIndex: dragSnapshot.isDragging ? 99999 : 'auto',
                                    boxShadow: dragSnapshot.isDragging ? '0 12px 40px rgba(0,0,0,0.45), 0 0 0 1px var(--dp-blue-500)' : 'none',
                                  }}
                                >
                                  {/* Drag handle + type */}
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <div {...dragProvided.dragHandleProps} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'var(--dp-text-tertiary)', opacity: 0.5 }}>
                                        <GripVertical size={14} />
                                      </div>
                                      <span style={{ fontSize: '12px' }}>{typeConfig.icon}</span>
                                      <span style={{
                                        fontSize: '10px', fontWeight: 600, textTransform: 'uppercase',
                                        letterSpacing: '0.4px', color: typeConfig.color, fontFamily: 'var(--dp-font)',
                                      }}>
                                        {typeConfig.label}
                                      </span>
                                    </div>
                                    {/* Card actions (visible on hover via CSS-in-JS workaround) */}
                                    <div className="dp-card-actions" style={{ display: 'flex', gap: '2px', opacity: 0, transition: 'opacity 0.2s' }}>
                                      {isEditing ? (
                                        <>
                                          <button onClick={salvarEdicao} style={{ width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Check size={12} style={{ color: '#34D399' }} />
                                          </button>
                                          <button onClick={cancelarEdicao} style={{ width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <X size={12} style={{ color: '#F87171' }} />
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => iniciarEdicao(nota)}
                                            style={{ width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: 'transparent', color: 'var(--dp-text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.background = 'rgba(13,174,255,0.08)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}
                                          >
                                            <Edit3 size={14} />
                                          </button>
                                          <button
                                            onClick={() => deletarNota(nota.id)}
                                            style={{ width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: 'transparent', color: 'var(--dp-text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                                            onMouseEnter={e => { e.currentTarget.style.color = '#F87171'; e.currentTarget.style.background = 'rgba(248,113,113,0.10)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Title */}
                                  <div style={{ marginBottom: '6px' }}>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editandoTitulo}
                                        onChange={e => setEditandoTitulo(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') salvarEdicao(); if (e.key === 'Escape') cancelarEdicao(); }}
                                        style={{
                                          ...S.input,
                                          padding: '6px 8px',
                                          fontSize: '13px',
                                          fontWeight: 600,
                                        }}
                                        autoFocus
                                      />
                                    ) : (
                                      <div style={{
                                        fontSize: '13px', fontWeight: 600, lineHeight: 1.3,
                                        color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                                      }}>
                                        {nota.titulo}
                                      </div>
                                    )}
                                  </div>

                                  {/* Body: note content or checklist */}
                                  {nota.tipo === 'nota_simples' ? (
                                    editandoConteudoNotaId === nota.id ? (
                                      <div style={{ marginBottom: '8px' }}>
                                        <textarea
                                          value={editandoConteudoTexto}
                                          onChange={e => setEditandoConteudoTexto(e.target.value)}
                                          onBlur={() => salvarEdicaoConteudo(nota.id)}
                                          onKeyDown={e => { if (e.key === 'Escape') cancelarEdicaoConteudo(); }}
                                          style={{
                                            ...S.input,
                                            resize: 'none',
                                            minHeight: '60px',
                                            border: '1px solid var(--dp-blue-500)',
                                            boxShadow: '0 0 0 3px rgba(13,174,255,0.15)',
                                          }}
                                          autoFocus
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px', marginTop: '6px' }}>
                                          <button onClick={() => salvarEdicaoConteudo(nota.id)} style={{ padding: '3px 8px', background: '#34D399', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontFamily: 'var(--dp-font)' }}>
                                            ✓ {t('ideasBoard.save')}
                                          </button>
                                          <button onClick={cancelarEdicaoConteudo} style={{ padding: '3px 8px', background: '#F87171', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontFamily: 'var(--dp-font)' }}>
                                            ✗ {t('ideasBoard.cancel')}
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        onDoubleClick={() => iniciarEdicaoConteudo(nota)}
                                        style={{
                                          fontSize: '12px',
                                          color: 'var(--dp-text-secondary)',
                                          lineHeight: 1.5,
                                          marginBottom: '8px',
                                          whiteSpace: 'pre-wrap',
                                          cursor: 'pointer',
                                          padding: '4px',
                                          borderRadius: '4px',
                                          transition: 'background 0.2s',
                                          fontFamily: 'var(--dp-font)',
                                        }}
                                        title={t('ideasBoard.double_click_edit')}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--dp-bg-card-hover)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                      >
                                        {nota.conteudo || t('ideasBoard.default_content')}
                                      </div>
                                    )
                                  ) : (
                                    /* Checklist */
                                    <div style={{ marginBottom: '8px' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
                                        {nota.tarefas && nota.tarefas.map(tarefa => {
                                          const isEditingTask = editandoTarefaNotaId === nota.id && editandoTarefaId === tarefa.id;
                                          return (
                                            <div key={tarefa.id} style={{
                                              display: 'flex', alignItems: 'center', gap: '8px',
                                              fontSize: '12px', padding: '3px 0', transition: 'all 0.2s',
                                              color: tarefa.concluida ? 'var(--dp-text-tertiary)' : 'var(--dp-text-secondary)',
                                              textDecoration: tarefa.concluida ? 'line-through' : 'none',
                                              fontFamily: 'var(--dp-font)',
                                            }}>
                                              <button
                                                onClick={() => toggleTarefa(nota, tarefa.id)}
                                                style={{
                                                  width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                                                  border: tarefa.concluida ? '2px solid #34D399' : '2px solid var(--dp-border-medium)',
                                                  background: tarefa.concluida ? '#34D399' : 'transparent',
                                                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                  transition: 'all 0.2s', fontSize: '9px', color: tarefa.concluida ? '#fff' : 'transparent',
                                                }}
                                              >
                                                {tarefa.concluida ? '✓' : ''}
                                              </button>
                                              {isEditingTask ? (
                                                <div style={{ flex: 1, display: 'flex', gap: '4px' }}>
                                                  <input
                                                    type="text"
                                                    value={editandoTarefaTexto}
                                                    onChange={e => setEditandoTarefaTexto(e.target.value)}
                                                    onBlur={(e) => {
                                                      const relatedTarget = e.relatedTarget;
                                                      if (relatedTarget && relatedTarget.getAttribute('data-add-task') === 'true') return;
                                                      salvarEdicaoTarefa(nota);
                                                    }}
                                                    onKeyDown={e => {
                                                      if (e.key === 'Enter') e.target.blur();
                                                      if (e.key === 'Escape') cancelarEdicaoTarefa();
                                                    }}
                                                    style={{
                                                      ...S.input,
                                                      flex: 1,
                                                      padding: '4px 8px',
                                                      fontSize: '12px',
                                                      border: '1px solid var(--dp-blue-500)',
                                                    }}
                                                    autoFocus
                                                  />
                                                  <button onClick={() => salvarEdicaoTarefa(nota)} style={{ padding: '2px 6px', background: '#34D399', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                    <Check size={10} style={{ color: '#fff' }} />
                                                  </button>
                                                  <button onClick={cancelarEdicaoTarefa} style={{ padding: '2px 6px', background: '#F87171', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                    <X size={10} style={{ color: '#fff' }} />
                                                  </button>
                                                </div>
                                              ) : (
                                                <span
                                                  onDoubleClick={() => iniciarEdicaoTarefa(nota.id, tarefa.id, tarefa.texto)}
                                                  style={{
                                                    flex: 1, cursor: 'pointer', padding: '2px 4px',
                                                    borderRadius: '4px', transition: 'background 0.2s',
                                                  }}
                                                  title={t('ideasBoard.double_click_edit')}
                                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--dp-bg-card-hover)'}
                                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                >
                                                  {tarefa.texto}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <button
                                        data-add-task="true"
                                        onClick={() => adicionarTarefa(nota)}
                                        style={{
                                          fontSize: '11px', color: 'var(--dp-blue-500)',
                                          background: 'transparent', border: 'none', cursor: 'pointer',
                                          fontWeight: 500, fontFamily: 'var(--dp-font)', opacity: 0.7,
                                          transition: 'opacity 0.2s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                        onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
                                      >
                                        {t('ideasBoard.add_task')}
                                      </button>

                                      {/* Progress bar */}
                                      {nota.tarefas && nota.tarefas.length > 0 && (() => {
                                        const pct = calcularProgresso(nota.tarefas);
                                        return (
                                          <div style={{ marginTop: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                              <span style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                                                {t('ideasBoard.progress')}
                                              </span>
                                              <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
                                                {pct}%
                                              </span>
                                            </div>
                                            <div style={{ height: '3px', borderRadius: '2px', background: 'var(--dp-border-subtle)' }}>
                                              <div style={{
                                                height: '100%', borderRadius: '2px',
                                                width: `${pct}%`,
                                                background: getProgressGradient(pct),
                                                transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
                                              }} />
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* Card footer: tags */}
                                  {nota.tags && nota.tags.length > 1 && (
                                    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                                      {nota.tags.slice(1).map((tag, ti) => (
                                        <span key={ti} style={{
                                          padding: '2px 6px', borderRadius: '9999px',
                                          fontSize: '9px', fontWeight: 600,
                                          background: 'rgba(167,139,250,0.10)', color: '#A78BFA',
                                          fontFamily: 'var(--dp-font)',
                                        }}>
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );

                              // Portal: card arrastado renderiza em document.body (fora de qualquer stacking context)
                              if (dragSnapshot.isDragging) {
                                return createPortal(child, document.body);
                              }
                              return child;
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>

                      {/* ── Column Footer ── */}
                      <div style={{ padding: '8px 10px', flexShrink: 0 }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => abrirModalNovaNota(coluna.id, 'nota_simples')}
                            style={{
                              flex: 1, padding: '8px', borderRadius: '8px',
                              border: '1px dashed var(--dp-border-medium, rgba(255,255,255,0.10))',
                              background: 'transparent',
                              color: 'var(--dp-text-tertiary)',
                              fontSize: '12px', fontWeight: 500, fontFamily: 'var(--dp-font)',
                              cursor: 'pointer', transition: 'all 0.25s',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.background = 'rgba(13,174,255,0.04)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}
                          >
                            {t('ideasBoard.add_note_short')}
                          </button>
                          <button
                            onClick={() => abrirModalNovaNota(coluna.id, 'checklist')}
                            style={{
                              flex: 1, padding: '8px', borderRadius: '8px',
                              border: '1px dashed var(--dp-border-medium, rgba(255,255,255,0.10))',
                              background: 'transparent',
                              color: 'var(--dp-text-tertiary)',
                              fontSize: '12px', fontWeight: 500, fontFamily: 'var(--dp-font)',
                              cursor: 'pointer', transition: 'all 0.25s',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.background = 'rgba(13,174,255,0.04)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}
                          >
                            {t('ideasBoard.add_checklist_short')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}

            {/* ── Add Column Card ── */}
            <div
              onClick={abrirModalNovaColuna}
              style={{
                minWidth: '280px', flexShrink: 0,
                border: '2px dashed var(--dp-border-medium, rgba(255,255,255,0.10))',
                borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.3s',
                color: 'var(--dp-text-tertiary)',
                fontSize: '14px', fontWeight: 500, fontFamily: 'var(--dp-font)',
                gap: '8px',
                maxHeight: 'calc(100vh - 180px)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.background = 'rgba(13,174,255,0.03)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}
            >
              {t('ideasBoard.add_column')}
            </div>

            {/* Empty state */}
            {colunas.length === 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '100%', minHeight: '400px',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '64px', opacity: 0.3, marginBottom: '16px' }}>💡</div>
                  <h3 style={{
                    fontSize: '20px', fontWeight: 600, marginBottom: '8px',
                    color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
                  }}>
                    {t('ideasBoard.no_columns_title')}
                  </h3>
                  <p style={{
                    fontSize: '14px', marginBottom: '24px',
                    color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
                  }}>
                    {t('ideasBoard.no_columns_text')}
                  </p>
                  <button onClick={abrirModalNovaColuna} style={S.btnPrimary}>
                    <Plus size={16} />
                    {t('ideasBoard.create_first_column')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </DragDropContext>
      </main>

      {/* ════════ Modal: New/Edit Column ════════ */}
      <Dialog open={showModalColuna} onOpenChange={setShowModalColuna}>
        <DialogContent className="dp-hide-dialog-x" aria-describedby={undefined} style={{
          background: 'var(--dp-bg-glass)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          padding: '28px',
          maxWidth: '460px',
          width: 'calc(100% - 32px)',
          zIndex: 9999,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <DialogTitle style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: 0 }}>
              {colunaEditando ? t('ideasBoard.edit_column') : t('ideasBoard.new_column')}
            </DialogTitle>
            <button onClick={() => setShowModalColuna(false)} style={{
              width: '32px', height: '32px', borderRadius: '8px',
              border: '1px solid var(--dp-border-subtle)', background: 'transparent',
              color: 'var(--dp-text-secondary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#F87171'; e.currentTarget.style.color = '#F87171'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={S.label}>{t('ideasBoard.column_name')}</label>
            <input
              type="text"
              value={formColuna.nome}
              onChange={e => setFormColuna({ ...formColuna, nome: e.target.value })}
              placeholder={t('ideasBoard.column_name_placeholder')}
              style={S.input}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.15)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
              onKeyDown={e => { if (e.key === 'Enter') salvarColuna(); }}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={S.label}>{t('ideasBoard.column_color')}</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(COLUMN_COLORS).map(([nome, cfg]) => (
                <button
                  key={nome}
                  onClick={() => setFormColuna({ ...formColuna, cor: nome })}
                  style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: cfg.hex, cursor: 'pointer',
                    border: formColuna.cor === nome ? '2px solid #fff' : '2px solid transparent',
                    boxShadow: formColuna.cor === nome ? '0 0 12px var(--dp-blue-glow)' : 'none',
                    transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 700, color: '#fff',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {formColuna.cor === nome ? '✓' : ''}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--dp-border-subtle)' }}>
            <button onClick={() => setShowModalColuna(false)} style={S.btnCancel}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--dp-text-secondary)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
            >
              {t('ideasBoard.cancel')}
            </button>
            <button onClick={salvarColuna} style={S.btnPrimary}
            >
              <Plus size={14} />
              {colunaEditando ? t('ideasBoard.save_changes') : t('ideasBoard.create_column')}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ════════ Modal: New Note ════════ */}
      <Dialog open={showModalNota} onOpenChange={setShowModalNota}>
        <DialogContent className="dp-hide-dialog-x" aria-describedby={undefined} style={{
          background: 'var(--dp-bg-glass)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          padding: '28px',
          maxWidth: '460px',
          width: 'calc(100% - 32px)',
          zIndex: 9999,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <DialogTitle style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: 0 }}>
              {tipoNovaNota === 'checklist' ? `✅ ${t('ideasBoard.new_checklist')}` : `📝 ${t('ideasBoard.new_note')}`}
            </DialogTitle>
            <button onClick={() => setShowModalNota(false)} style={{
              width: '32px', height: '32px', borderRadius: '8px',
              border: '1px solid var(--dp-border-subtle)', background: 'transparent',
              color: 'var(--dp-text-secondary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#F87171'; e.currentTarget.style.color = '#F87171'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={S.label}>{t('ideasBoard.card_title')}</label>
            <input
              type="text"
              value={formNota.titulo}
              onChange={e => setFormNota({ ...formNota, titulo: e.target.value })}
              placeholder={t('ideasBoard.card_title_placeholder')}
              style={S.input}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.15)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.boxShadow = 'none'; }}
              onKeyDown={e => { if (e.key === 'Enter') criarNota(); }}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={S.label}>{t('ideasBoard.card_color')}</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(NOTE_COLORS).map(([nome, cfg]) => (
                <button
                  key={nome}
                  onClick={() => setFormNota({ ...formNota, cor: nome })}
                  style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    background: cfg.accent, cursor: 'pointer',
                    border: formNota.cor === nome ? '2px solid #fff' : '2px solid transparent',
                    boxShadow: formNota.cor === nome ? '0 0 12px var(--dp-blue-glow)' : 'none',
                    transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 700, color: '#fff',
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {formNota.cor === nome ? '✓' : ''}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--dp-border-subtle)' }}>
            <button onClick={() => setShowModalNota(false)} style={S.btnCancel}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--dp-text-secondary)'; e.currentTarget.style.color = 'var(--dp-text-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
            >
              {t('ideasBoard.cancel')}
            </button>
            <button onClick={criarNota} style={S.btnPrimary}
            >
              <Plus size={14} />
              {tipoNovaNota === 'checklist' ? t('ideasBoard.create_checklist') : t('ideasBoard.create_note')}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Injected CSS ── */}
      <style>{`
        @keyframes dpKanbanFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .k-card:hover .dp-card-actions,
        [style*="cursor: grab"]:hover .dp-card-actions {
          opacity: 1 !important;
        }
        /* Hide the built-in Dialog close X (from shadcn DialogContent) when we have our own */
        .dp-hide-dialog-x > button.absolute { display: none !important; }
        /* Scrollbar for kanban columns */
        .kanban-col-body::-webkit-scrollbar { width: 4px; }
        .kanban-col-body::-webkit-scrollbar-track { background: transparent; }
        .kanban-col-body::-webkit-scrollbar-thumb { background: rgba(13, 174, 255, 0.2); border-radius: 2px; }
        /* Dragged card must appear ABOVE all columns.
           @hello-pangea/dnd uses position:fixed on the dragged clone. */
        [data-rbd-draggable-context-id][style*="position: fixed"] {
          z-index: 99999 !important;
          pointer-events: auto !important;
        }
      `}</style>
    </div>
  );
};

export default QuadroIdeiasPageKanban;
